import { __assign, __awaiter, __generator } from "tslib";
import AlloyTouch from 'phy-touch';
import AlloyFinger from 'alloyfinger';
import transform from 'css3transform';
import { calcClippedResult, nextFrame } from './utils';
var LONG_TAP_DURATION = 350;
var DOUBLE_TAP_MAX_GAP = 350;
var TouchGesture = (function () {
    function TouchGesture(props) {
        var _this = this;
        this.props = props;
        this.isMultipointTouching = false;
        this.multipointStartScale = 1;
        this.currentImageScale = 1;
        this.originalSize = null;
        this.preventDefault = function (e) {
            e.preventDefault();
        };
        this.handleHorizontalScrollEnd = function () {
            var page = _this.pageRef.current;
            if (!page)
                return;
            var threshold = window.innerWidth * 0.15;
            if (_this.valueX > _this.maxPageX) {
                if (!_this.isMultipointTouching && _this.valueX > _this.maxPageX + threshold) {
                    _this.prevPage();
                }
                else {
                    _this.toCurrentPageLeft();
                }
            }
            else if (_this.valueX < _this.minPageX) {
                if (!_this.isMultipointTouching && _this.valueX < _this.minPageX - threshold) {
                    _this.nextPage();
                }
                else {
                    _this.toCurrentPageRight();
                }
            }
        };
        this.handleVerticalTouchMove = function () {
            if (_this.isMultipointTouching)
                return;
            var safeArea = _this.safeAreaRef.current;
            var mask = _this.maskRef.current;
            if (!safeArea || !mask)
                return;
            var verticalTouch = _this.verticalTouch;
            var _a = _this.props, allowSwipeDownToClose = _a.allowSwipeDownToClose, allowSwipeUpToClose = _a.allowSwipeUpToClose;
            var distance = (verticalTouch.preY - verticalTouch.start) / safeArea.clientHeight / 3;
            if (allowSwipeDownToClose && verticalTouch.isAtMax() ||
                allowSwipeUpToClose && verticalTouch.isAtMin()) {
                distance = Math.abs(distance);
                verticalTouch.startTime = Date.now();
            }
            else {
                distance = 0;
            }
            mask.style.background = "rgba(0,0,0," + (1 - distance) + ")";
        };
        this.handleVerticalTouchEnd = function () {
            var safeArea = _this.safeAreaRef.current;
            var mask = _this.maskRef.current;
            if (!safeArea || !mask)
                return;
            var verticalTouch = _this.verticalTouch;
            var _a = _this.props, allowSwipeDownToClose = _a.allowSwipeDownToClose, allowSwipeUpToClose = _a.allowSwipeUpToClose, onClose = _a.onClose;
            var threshold = safeArea.clientHeight * 0.1;
            var imageWrapper = _this.imageWrapperRef.current;
            if (!_this.isMultipointTouching && imageWrapper && (allowSwipeDownToClose && verticalTouch.isAtMax() && imageWrapper.translateY > _this.maxY + threshold ||
                allowSwipeUpToClose && verticalTouch.isAtMin() && imageWrapper.translateY < _this.minY - threshold)) {
                onClose(true);
            }
            mask.style.background = 'rgba(0,0,0,1)';
        };
        this.handleTouchStart = function (e) {
            _this.isMultipointTouching = false;
            _this.touchStartEvent = e;
            _this.lastPositionalTouchEvent = e;
            _this.longTouchTimeout = setTimeout(function () {
                if (!_this.isMultipointTouching) {
                    _this.props.onLongTap();
                }
            }, LONG_TAP_DURATION);
        };
        this.handleTouchMove = function (e) {
            if (_this.longTouchTimeout) {
                if (Math.abs(_this.touchStartEvent.touches[0].screenX - _this.lastPositionalTouchEvent.touches[0].screenX) > 20 ||
                    Math.abs(_this.touchStartEvent.touches[0].screenY - _this.lastPositionalTouchEvent.touches[0].screenY) > 20) {
                    clearTimeout(_this.longTouchTimeout);
                    _this.longTouchTimeout = null;
                    _this.props.onLongTapCancel();
                }
            }
            _this.lastPositionalTouchEvent = e;
        };
        this.handleTouchEnd = function (e) {
            if (_this.longTouchTimeout) {
                clearTimeout(_this.longTouchTimeout);
                _this.longTouchTimeout = null;
            }
            if (e.timeStamp - _this.touchStartEvent.timeStamp < 100 &&
                Math.abs(_this.touchStartEvent.touches[0].screenX - _this.lastPositionalTouchEvent.touches[0].screenX) < 20 &&
                Math.abs(_this.touchStartEvent.touches[0].screenY - _this.lastPositionalTouchEvent.touches[0].screenY) < 20) {
                if (_this.doubleTapTimeout) {
                    _this.handleDoubleTap();
                }
                else {
                    _this.handleSingleTap();
                }
            }
        };
        this.handleMultipointTouchStart = function () {
            _this.isMultipointTouching = true;
            _this.horizontalTouch.isTouchStart = false;
            _this.handleHorizontalScrollEnd();
            _this.verticalTouch.isTouchStart = false;
            _this.handleVerticalTouchEnd();
            _this.multipointStartScale = _this.currentImageScale;
        };
        this.handleMultipointPinch = function (e) {
            if (!_this.props.canPinchZoom()) {
                return;
            }
            var newImageScale = e.zoom * _this.multipointStartScale;
            _this.scaleTo(newImageScale, 0, e);
        };
        Object.assign(this, props);
        this.initBehaviors();
    }
    TouchGesture.prototype.destroy = function () {
        clearTimeout(this.doubleTapTimeout);
        this.horizontalTouch.stop();
        this.verticalTouch.stop();
        this.multipointTouch.destroy();
    };
    TouchGesture.prototype.updateProps = function (newProps) {
        var prevIndex = this.props.currentIndex;
        var nextIndex = newProps.currentIndex;
        var isNewPage = typeof nextIndex === 'number' && nextIndex !== prevIndex;
        Object.assign(this.props, newProps);
        Object.assign(this, newProps);
        if (isNewPage) {
            this.initForCurrentImage();
            this.toCurrentPageLeft();
            this.scaleOnce();
        }
    };
    TouchGesture.prototype.initBehaviors = function () {
        this.blockEventDefaults();
        this.initHorizontalTouch();
        this.initVerticalTouch();
        this.initMultipointTouch();
        this.initForCurrentImage();
    };
    TouchGesture.prototype.blockEventDefaults = function () {
        var wrapper = this.wrapperRef.current;
        wrapper.addEventListener('dragstart', this.preventDefault);
        wrapper.addEventListener('dragover', this.preventDefault);
        wrapper.addEventListener('touchmove', this.preventDefault);
        wrapper.addEventListener('touchend', this.preventDefault);
        wrapper.addEventListener('click', this.preventDefault);
        if (this.props.disableSystemLongTap) {
            wrapper.addEventListener('touchstart', this.preventDefault);
        }
    };
    TouchGesture.prototype.initHorizontalTouch = function () {
        var _this = this;
        var wrapper = this.wrapperRef.current;
        transform(wrapper);
        this.horizontalTouch = new AlloyTouch({
            touch: wrapper,
            target: this,
            property: 'valueX',
            min: 0,
            max: 0,
            deceleration: 0.01,
            vertical: false,
            lockDirection: false,
            touchEnd: this.handleHorizontalScrollEnd,
            animationEnd: this.handleHorizontalScrollEnd,
        });
        Object.defineProperty(this.horizontalTouch, 'min', { get: function () { return _this.minX; } });
        Object.defineProperty(this.horizontalTouch, 'max', { get: function () { return _this.maxX; } });
        Object.defineProperty(this.horizontalTouch, 'lockDirection', { get: function () { return _this.lockDirectionX; } });
    };
    Object.defineProperty(TouchGesture.prototype, "valueX", {
        get: function () {
            var wrapper = this.wrapperRef.current;
            if (!wrapper)
                return 0;
            return wrapper.translateX;
        },
        set: function (value) {
            var wrapper = this.wrapperRef.current;
            if (!wrapper)
                return;
            wrapper.translateX = value;
            this.updateWidgetsVisibility();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TouchGesture.prototype, "minX", {
        get: function () {
            var page = this.pageRef.current;
            var safeArea = this.safeAreaRef.current;
            if (!page || !safeArea)
                return 0;
            var pageNum = this.props.images.length;
            return -((pageNum - 2) * safeArea.clientWidth + page.clientWidth);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TouchGesture.prototype, "maxX", {
        get: function () {
            return 0;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TouchGesture.prototype, "minPageX", {
        get: function () {
            var page = this.pageRef.current;
            var safeArea = this.safeAreaRef.current;
            if (!page || !safeArea)
                return 0;
            return (1 - this.props.currentIndex) * safeArea.clientWidth - page.clientWidth;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TouchGesture.prototype, "maxPageX", {
        get: function () {
            var safeArea = this.safeAreaRef.current;
            if (!safeArea)
                return 0;
            return -this.props.currentIndex * safeArea.clientWidth;
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TouchGesture.prototype, "lockDirectionX", {
        get: function () {
            return this.currentImageScale <= 1;
        },
        enumerable: false,
        configurable: true
    });
    TouchGesture.prototype.initVerticalTouch = function () {
        var _this = this;
        var wrapper = this.wrapperRef.current;
        var imageWrapper = this.imageWrapperRef.current;
        transform(imageWrapper);
        this.verticalTouch = new AlloyTouch({
            touch: wrapper,
            target: this,
            property: 'valueY',
            min: 0,
            max: 0,
            deceleration: 0.01,
            outFactor: 0.6,
            touchMove: this.handleVerticalTouchMove,
            touchEnd: this.handleVerticalTouchEnd,
        });
        Object.defineProperty(this.verticalTouch, 'min', { get: function () { return _this.minY; } });
        Object.defineProperty(this.verticalTouch, 'max', { get: function () { return _this.maxY; } });
    };
    Object.defineProperty(TouchGesture.prototype, "valueY", {
        get: function () {
            var imageWrapper = this.imageWrapperRef.current;
            if (!imageWrapper)
                return 0;
            return imageWrapper.translateY;
        },
        set: function (value) {
            var imageWrapper = this.imageWrapperRef.current;
            if (!imageWrapper)
                return;
            imageWrapper.translateY = value;
            this.updateWidgetsVisibility();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TouchGesture.prototype, "minY", {
        get: function () {
            var safeArea = this.safeAreaRef.current;
            var imageWrapper = this.imageWrapperRef.current;
            if (!safeArea || !imageWrapper)
                return 0;
            return -Math.max(0, imageWrapper.clientHeight - safeArea.clientHeight);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(TouchGesture.prototype, "maxY", {
        get: function () {
            return 0;
        },
        enumerable: false,
        configurable: true
    });
    TouchGesture.prototype.initMultipointTouch = function () {
        var wrapper = this.wrapperRef.current;
        this.multipointTouch = new AlloyFinger(wrapper, {
            touchStart: this.handleTouchStart,
            touchMove: this.handleTouchMove,
            touchEnd: this.handleTouchEnd,
            multipointStart: this.handleMultipointTouchStart,
            pinch: this.handleMultipointPinch,
        });
        this.scaleOnce();
    };
    TouchGesture.prototype.initForCurrentImage = function () {
        var _this = this;
        var imageWrapper = this.imageWrapperRef.current;
        var image = this.imageRef.current;
        if (!imageWrapper || !image) {
            return;
        }
        transform(imageWrapper);
        var currentIndex = this.props.currentIndex;
        if (image.complete) {
            this.updateImageOriginalSize();
        }
        else {
            image.addEventListener('load', function () {
                if (currentIndex !== _this.props.currentIndex) {
                    return;
                }
                _this.updateImageOriginalSize();
            });
        }
    };
    TouchGesture.prototype.prevPage = function () {
        this.scaleOnce();
        this.valueY = 0;
        var _a = this.props, onChangePage = _a.onChangePage, currentIndex = _a.currentIndex;
        onChangePage(Math.max(0, currentIndex - 1));
    };
    TouchGesture.prototype.nextPage = function () {
        var overScroll = this.valueX - this.minPageX;
        this.scaleOnce();
        this.valueX = this.minPageX + overScroll;
        this.valueY = 0;
        var _a = this.props, onChangePage = _a.onChangePage, images = _a.images, currentIndex = _a.currentIndex;
        onChangePage(Math.min(images.length - 1, currentIndex + 1));
    };
    TouchGesture.prototype.handleDoubleTap = function () {
        if (Math.abs(this.lastPositionalTouchEvent.touches[0].screenX - this.firstTapPointX) < 20 &&
            Math.abs(this.lastPositionalTouchEvent.touches[0].screenY - this.firstTapPointY) < 20) {
            clearTimeout(this.doubleTapTimeout);
            this.doubleTapTimeout = null;
            var scale = this.currentImageScale;
            if (scale >= this.props.scaleOnDoubleTap || scale < 1) {
                this.scaleTo(1, 200, this.lastPositionalTouchEvent);
            }
            else {
                this.scaleTo(this.props.scaleOnDoubleTap, 200, this.lastPositionalTouchEvent);
            }
        }
    };
    TouchGesture.prototype.handleSingleTap = function () {
        var _this = this;
        this.firstTapPointX = this.lastPositionalTouchEvent.touches[0].screenX;
        this.firstTapPointY = this.lastPositionalTouchEvent.touches[0].screenY;
        var _a = this.props, onTap = _a.onTap, onClose = _a.onClose, allowTapToClose = _a.allowTapToClose;
        this.doubleTapTimeout = setTimeout(function () {
            onTap();
            if (allowTapToClose && !_this.isMultipointTouching) {
                onClose();
            }
            _this.doubleTapTimeout = null;
        }, DOUBLE_TAP_MAX_GAP);
    };
    TouchGesture.prototype.toCurrentPageLeft = function (duration) {
        var _this = this;
        if (duration === void 0) { duration = 200; }
        setTimeout(function () {
            _this.horizontalTouch.stop();
            _this.horizontalTouch.to(_this.maxPageX, duration);
        }, 0);
    };
    TouchGesture.prototype.toCurrentPageRight = function (duration) {
        var _this = this;
        if (duration === void 0) { duration = 200; }
        setTimeout(function () {
            _this.horizontalTouch.stop();
            _this.horizontalTouch.to(_this.minPageX, duration);
        }, 0);
    };
    TouchGesture.prototype.scaleTo = function (scale, duration, event) {
        if (scale === void 0) { scale = 1; }
        if (duration === void 0) { duration = 200; }
        return __awaiter(this, void 0, void 0, function () {
            var safeArea, imageWrapper, wrapper, oldScale, oldX, oldY, requestedScale, centerPointScreenX, centerPointScreenY, touches, imageTranslateX, imageTranslateY, centerPointImageX, centerPointImageY, newImageTranslateX, newImageTranslateY, deltaScale, deltaX, deltaY, time, frame, progress;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        safeArea = this.safeAreaRef.current;
                        imageWrapper = this.imageWrapperRef.current;
                        wrapper = this.wrapperRef.current;
                        if (!safeArea || !imageWrapper || !wrapper)
                            return [2];
                        oldScale = this.currentImageScale;
                        oldX = wrapper.translateX - this.maxPageX;
                        oldY = imageWrapper.translateY;
                        requestedScale = Math.min(scale, this.props.maxScale);
                        centerPointScreenX = safeArea.clientWidth / 2;
                        centerPointScreenY = safeArea.clientHeight / 2;
                        if (event && event.touches.length) {
                            touches = Array.from(event.touches);
                            centerPointScreenX = touches
                                .map(function (touch) { return touch.pageX - window.scrollX; })
                                .reduce(function (a, b) { return a + b; }, 0) / touches.length;
                            centerPointScreenY = touches
                                .map(function (touch) { return touch.pageY - window.scrollY; })
                                .reduce(function (a, b) { return a + b; }, 0) / touches.length;
                        }
                        imageTranslateX = wrapper.translateX - this.maxPageX;
                        imageTranslateY = imageWrapper.translateY;
                        centerPointImageX = (centerPointScreenX - imageTranslateX) / oldScale;
                        centerPointImageY = (centerPointScreenY - imageTranslateY) / oldScale;
                        newImageTranslateX = centerPointScreenX - centerPointImageX * requestedScale;
                        newImageTranslateY = centerPointScreenY - centerPointImageY * requestedScale;
                        deltaScale = requestedScale - oldScale;
                        deltaX = newImageTranslateX - oldX;
                        deltaY = newImageTranslateY - oldY;
                        time = 0;
                        frame = 0;
                        _a.label = 1;
                    case 1:
                        if (!(time < duration)) return [3, 3];
                        progress = time / duration;
                        this.scaleOnce(oldScale + deltaScale * progress);
                        this.moveOnce(oldX + deltaX * progress, oldY + deltaY * progress);
                        time += frame;
                        return [4, nextFrame()];
                    case 2:
                        frame = _a.sent();
                        return [3, 1];
                    case 3:
                        this.scaleOnce(requestedScale);
                        this.moveOnce(newImageTranslateX, newImageTranslateY);
                        return [2];
                }
            });
        });
    };
    TouchGesture.prototype.moveTo = function (x, y, duration) {
        if (duration === void 0) { duration = 200; }
        return __awaiter(this, void 0, void 0, function () {
            var wrapper, imageWrapper, oldX, oldY, deltaX, deltaY, time, frame, progress;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        wrapper = this.wrapperRef.current;
                        imageWrapper = this.imageWrapperRef.current;
                        if (!imageWrapper || !wrapper)
                            return [2];
                        oldX = wrapper.translateX - this.maxPageX;
                        oldY = imageWrapper.translateY;
                        deltaX = x - oldX;
                        deltaY = y - oldY;
                        if (!(oldX !== x || oldY !== y)) return [3, 4];
                        time = 0;
                        frame = 0;
                        _a.label = 1;
                    case 1:
                        if (!(time < duration)) return [3, 3];
                        progress = time / duration;
                        this.moveOnce(oldX + deltaX * progress, oldY + deltaY * progress);
                        time += frame;
                        return [4, nextFrame()];
                    case 2:
                        frame = _a.sent();
                        return [3, 1];
                    case 3:
                        this.moveOnce(x, y);
                        _a.label = 4;
                    case 4: return [2];
                }
            });
        });
    };
    TouchGesture.prototype.updateImageOriginalSize = function () {
        this.originalSize = this.calcImageOriginalSize();
        this.scaleOnce();
    };
    TouchGesture.prototype.getClippedResult = function () {
        var _a = this.props, images = _a.images, currentIndex = _a.currentIndex;
        var clipStyle = images[currentIndex].clipStyle;
        if (!clipStyle) {
            return null;
        }
        return calcClippedResult(this.imageRef.current, clipStyle);
    };
    TouchGesture.prototype.calcImageOriginalSize = function () {
        var image = this.imageRef.current;
        if (!image || !image.complete) {
            return null;
        }
        var clippedResult = this.getClippedResult();
        var naturalWidth = image.naturalWidth, naturalHeight = image.naturalHeight;
        if (clippedResult) {
            return __assign({ naturalWidth: naturalWidth,
                naturalHeight: naturalHeight }, clippedResult);
        }
        return {
            clippedWidth: naturalWidth,
            clippedHeight: naturalHeight,
            clippedLeft: 0,
            clippedTop: 0,
            naturalWidth: naturalWidth,
            naturalHeight: naturalHeight,
        };
    };
    TouchGesture.prototype.calcImageFitSize = function () {
        var safeArea = this.safeAreaRef.current;
        var image = this.imageRef.current;
        if (!safeArea || !image || !image.complete || !this.originalSize) {
            return null;
        }
        var _a = this.originalSize, naturalWidth = _a.naturalWidth, naturalHeight = _a.naturalHeight, clippedWidth = _a.clippedWidth, clippedHeight = _a.clippedHeight, clippedLeft = _a.clippedLeft, clippedTop = _a.clippedTop;
        var fitWidth = Math.min(clippedWidth, safeArea.clientWidth);
        var imageFitScale = fitWidth / clippedWidth;
        var fitHeight = clippedHeight * imageFitScale;
        var minWidth = Math.min(safeArea.clientWidth, clippedWidth);
        var minHeight = Math.min(safeArea.clientHeight, clippedHeight);
        return {
            fitWidth: fitWidth,
            fitHeight: fitHeight,
            minWidth: minWidth,
            minHeight: minHeight,
            imageFitWidth: naturalWidth * imageFitScale,
            imageFitHeight: naturalHeight * imageFitScale,
            marginLeftRatio: clippedLeft / clippedWidth,
            marginTopRatio: clippedTop / clippedHeight,
        };
    };
    TouchGesture.prototype.scaleOnce = function (scale) {
        if (scale === void 0) { scale = 1; }
        var imageWrapper = this.imageWrapperRef.current;
        var imageSize = this.calcImageFitSize();
        if (!imageSize) {
            return;
        }
        var fitWidth = imageSize.fitWidth, fitHeight = imageSize.fitHeight, minWidth = imageSize.minWidth, minHeight = imageSize.minHeight, imageFitWidth = imageSize.imageFitWidth, imageFitHeight = imageSize.imageFitHeight, marginLeftRatio = imageSize.marginLeftRatio, marginTopRatio = imageSize.marginTopRatio;
        var requestedWidth = fitWidth * scale;
        var requestedHeight = fitHeight * scale;
        var newScale = scale;
        var maxScale = this.props.maxScale;
        if (requestedWidth < minWidth && requestedHeight < minHeight) {
            newScale *= Math.min(minWidth / requestedWidth, minHeight / requestedHeight);
        }
        else if (scale > maxScale) {
            newScale = maxScale;
        }
        var wrapperWidth = fitWidth * newScale;
        var wrapperHeight = fitHeight * newScale;
        imageWrapper.style.width = wrapperWidth + "px";
        imageWrapper.style.height = wrapperHeight + "px";
        var image = this.imageRef.current;
        image.style.width = imageFitWidth * newScale + "px";
        image.style.height = imageFitHeight * newScale + "px";
        image.style.marginLeft = "-" + marginLeftRatio * wrapperWidth + "px";
        image.style.marginTop = "-" + marginTopRatio * wrapperHeight + "px";
        this.currentImageScale = newScale;
        return newScale;
    };
    TouchGesture.prototype.moveOnce = function (x, y) {
        var safeArea = this.safeAreaRef.current;
        var imageWrapper = this.imageWrapperRef.current;
        if (!safeArea || !imageWrapper)
            return;
        var imageWidth = imageWrapper.clientWidth;
        var imageHeight = imageWrapper.clientHeight;
        var safeAreaWidth = safeArea.clientWidth;
        var safeAreaHeight = safeArea.clientHeight;
        var newMoveX = x;
        var newMoveY = y;
        if (imageWidth < safeAreaWidth) {
            newMoveX = 0;
        }
        else {
            newMoveX = Math.max(Math.min(0, newMoveX), safeAreaWidth - imageWidth);
        }
        if (imageHeight < safeAreaHeight) {
            newMoveY = 0;
        }
        else {
            newMoveY = Math.min(Math.max(this.minY, newMoveY), this.maxY);
        }
        this.valueX = -this.props.currentIndex * safeArea.clientWidth + newMoveX;
        this.valueY = newMoveY;
        return [newMoveX, newMoveY];
    };
    TouchGesture.prototype.updateWidgetsVisibility = function () {
        var isWidgetsVisible = this.valueY === 0 && this.currentImageScale <= 1;
        this.props.onWidgetsVisibilityChange(isWidgetsVisible);
    };
    return TouchGesture;
}());
export default TouchGesture;
